import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack, CheckCircle } from '@mui/icons-material';
import { appStore } from '../stores/AppStore';
import { trackButtonClick, trackExternalMessage } from '../utils/analytics';

interface ReceiveByCodePageProps {
  onBack: () => void;
}

const ReceiveByCodePage: React.FC<ReceiveByCodePageProps> = observer(({ onBack }) => {
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
    setCode(value);
    setCodeError('');
  };

  const handleReceiveClick = async () => {
    if (code.length !== 6) {
      setCodeError('Code must be 6 digits');
      return;
    }

    trackButtonClick('Receive Message by Code', 'Receive By Code Page');
    trackExternalMessage('receive', 'code');

    // Clear previous errors
    setCodeError('');
    appStore.externalMessageError = null;

    try {
      await appStore.receiveExternalMessageByCode(code);
      // Reset code after successful receive
      setCode('');
      // Navigate back after a short delay
      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (err: any) {
      // Show error message - prefer store error if available, otherwise use caught error
      const errorMessage = appStore.externalMessageError || err.message || 'Failed to receive message';
      setCodeError(errorMessage);
    }
  };

  const handleBack = () => {
    trackButtonClick('Back', 'Receive By Code Page');
    setCode('');
    setCodeError('');
    appStore.externalMessageError = null;
    onBack();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(10px)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={handleBack} sx={{ color: '#FFFFFF', mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
              Receive by Code
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2, opacity: 0.8 }}>
            Enter the 6-digit code to receive the message
          </Typography>

          <TextField
            fullWidth
            placeholder="000000"
            value={code}
            onChange={handleCodeChange}
            error={!!codeError}
            helperText={codeError}
            inputProps={{
              maxLength: 6,
              style: {
                textAlign: 'center',
                fontSize: '32px',
                letterSpacing: '8px',
                fontFamily: 'monospace',
                fontWeight: 'bold',
              },
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#FFFFFF',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.7)' },
                '&.Mui-error fieldset': { borderColor: '#f44336' },
              },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255, 255, 255, 0.5)' },
              '& .MuiFormHelperText-root': { color: '#f44336' },
            }}
          />

          {/* Show error from store or local state */}
          {(appStore.externalMessageError || codeError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {appStore.externalMessageError || codeError}
            </Alert>
          )}

          {appStore.externalMessageSuccess && (
            <Alert
              severity="success"
              icon={<CheckCircle />}
              sx={{ mb: 2 }}
            >
              Message received successfully!
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={
              appStore.isCreatingExternalMessage ? (
                <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
              ) : (
                <CheckCircle />
              )
            }
            onClick={handleReceiveClick}
            fullWidth
            disabled={code.length !== 6 || appStore.isCreatingExternalMessage}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.4)' },
              '&:disabled': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            {appStore.isCreatingExternalMessage ? 'Receiving...' : 'Receive Message'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
});

export default ReceiveByCodePage;

